/**
 * 渠道账号未读消息数 - Pinia store
 *
 * 轮询 GET /api/chat-records/accounts/unread，供三处使用：
 * 1. 访客会话左侧账号列表的未读徽标
 * 2. 会话列表「实时对话」tab 的角标
 * 3. 新消息声音提醒
 *
 * 声音提醒以「未读数比上一轮变大」为准，而不是靠 IM 推送：
 * IM 只推坐席已绑定账号下的消息，覆盖不到全部账号；轮询接口按可见账号统计，才是完整口径。
 *
 * 轮询放在全局而非访客会话页内，坐席停在任何页面都能收到提醒。
 */
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { chatRecordsApi } from '@/api';
import { useUserStore } from '@/stores/user';
import { playNewMessageSound } from '@/utils/notificationSound';
import { MSG_TYPE_LABELS } from '@/constants/sessions';
import type {
  AccountLastUserMessage,
  AccountNewMessageNotice,
  AccountUnreadItem,
  AccountUnreadResult,
} from '@/types/chatRecords';

/** 轮询间隔 */
const POLL_MS = 5000;

/** 单个账号的三档未读数 */
type AccountUnread = Pick<
  AccountUnreadItem,
  'unread_count' | 'mine_unread_count' | 'other_unread_count'
>;

const EMPTY_ACCOUNT_UNREAD: AccountUnread = {
  unread_count: 0,
  mine_unread_count: 0,
  other_unread_count: 0,
};

export const useAccountUnreadStore = defineStore('accountUnread', () => {
  /** 各账号未读数，key 为 account_id */
  const unreadMap = ref<Record<number, AccountUnread>>({});
  /** 可见账号范围内的未读总数 */
  const totalUnread = ref(0);
  /** 其中分配给当前坐席的部分 */
  const totalMineUnread = ref(0);
  /** 其中未分配或属于其他坐席的部分 */
  const totalOtherUnread = ref(0);

  /** 本轮新消息提醒；每次有新增时整体替换，供全局 Toast 逐条入列 */
  const newMessageNotices = ref<AccountNewMessageNotice[]>([]);

  /** 上一轮的未读数；null 表示还没有基线，首轮不触发提示音 */
  let lastMine: number | null = null;
  let lastOther: number | null = null;
  let noticeSequence = 0;
  /** 已弹出兜底文案、等待后续轮询补齐发送人和消息内容的提醒 key */
  let pendingDetailNoticeKey: number | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let polling = false;
  /** 账号切换版本号：阻止退出前已经发出的请求在新账号登录后回写。 */
  let requestVersion = 0;

  /** 开关取值：后端字段 NOT NULL DEFAULT 1，取不到时按开启 */
  const isOn = (key: 'personal_msg_sound' | 'public_msg_sound') => {
    const me = useUserStore().user;
    if (!me) return false;
    return Number(me[key] ?? 1) === 1;
  };

  /** 按消息类型生成可读正文，非文本消息优先展示类型名称 */
  const noticeContent = (message: AccountLastUserMessage) => {
    const typeLabel = MSG_TYPE_LABELS[String(message?.msg_type ?? '')];
    return typeLabel || String(message?.content ?? '').trim() || '您有新的访客消息';
  };

  /** 沿用原有总未读增量口径，两个提醒开关分别控制「我的」和「实时对话」 */
  const shouldNotify = (mine: number, other: number) => {
    const mineUp = lastMine !== null && mine > lastMine;
    const otherUp = lastOther !== null && other > lastOther;
    return (mineUp && isOn('personal_msg_sound')) || (otherUp && isOn('public_msg_sound'));
  };

  /** 生成弹窗展示数据；message 暂缺时先保留兜底，下一轮用同 key 更新 */
  const buildNewMessageNotice = (
    key: number,
    message?: AccountLastUserMessage,
  ): AccountNewMessageNotice => {
    const sessionId = Number(message?.session_id);
    const accountId = Number(message?.account_id);
    return {
      key,
      ...(Number.isFinite(sessionId) && sessionId > 0 ? { session_id: sessionId } : {}),
      ...(Number.isFinite(accountId) && accountId > 0 ? { account_id: accountId } : {}),
      nickname: String(message?.user_info?.nickname ?? '').trim(),
      content: message ? noticeContent(message) : '您有新的访客消息',
      created_at: String(message?.created_at ?? '').trim() || undefined,
    };
  };

  /**
   * 拉一次未读数。
   *
   * 轮询高频，失败必须静默：否则接口一抖动，响应拦截器会按这个频率反复弹错误 toast 刷屏。
   * 单次失败保留上一轮数据，下一轮自然补上。
   */
  const fetchOnce = async () => {
    const currentVersion = requestVersion;
    const requestToken = localStorage.getItem('token');
    try {
      const res = (await chatRecordsApi.accountsUnread()) as AccountUnreadResult | undefined;
      if (
        currentVersion !== requestVersion ||
        !requestToken ||
        localStorage.getItem('token') !== requestToken
      ) {
        return;
      }
      const list = res?.list ?? [];
      const map: Record<number, AccountUnread> = {};
      list.forEach((item) => {
        map[Number(item.account_id)] = {
          unread_count: Number(item.unread_count ?? 0),
          mine_unread_count: Number(item.mine_unread_count ?? 0),
          other_unread_count: Number(item.other_unread_count ?? 0),
        };
      });
      unreadMap.value = map;

      const mine = Number(res?.total_mine_unread_count ?? 0);
      const other = Number(res?.total_other_unread_count ?? 0);
      /** 首轮只记基线不提醒，后续恢复原有总未读增量触发口径 */
      if (shouldNotify(mine, other)) {
        playNewMessageSound(res?.last_user_message?.message_id);
        const key = ++noticeSequence;
        const message = res?.last_user_message ?? undefined;
        pendingDetailNoticeKey = message ? null : key;
        newMessageNotices.value = [buildNewMessageNotice(key, message)];
      } else if (pendingDetailNoticeKey !== null) {
        /** 后端若晚一轮补齐消息详情，用原 key 更新已经展示的弹窗 */
        const message = res?.last_user_message ?? undefined;
        if (message) {
          newMessageNotices.value = [buildNewMessageNotice(pendingDetailNoticeKey, message)];
          pendingDetailNoticeKey = null;
        }
      }

      totalUnread.value = Number(res?.total_unread_count ?? 0);
      totalMineUnread.value = mine;
      totalOtherUnread.value = other;
      lastMine = mine;
      lastOther = other;
    } catch {
      /* 保留上一轮数据，等下一轮重试 */
    }
  };

  /**
   * 用「上一轮结束后再计时」而不是 setInterval：
   * 接口稍慢就会与下一次重叠，setInterval 会让请求越堆越多。
   */
  const schedule = () => {
    const currentVersion = requestVersion;
    timer = setTimeout(async () => {
      await fetchOnce();
      if (polling && currentVersion === requestVersion) schedule();
    }, POLL_MS);
  };

  const start = () => {
    if (polling) return;
    polling = true;
    const currentVersion = requestVersion;
    fetchOnce().then(() => {
      if (polling && currentVersion === requestVersion) schedule();
    });
  };

  const stop = () => {
    polling = false;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  /** 退出登录时清空，避免下一个账号读到上一个人的未读数与基线 */
  const reset = () => {
    requestVersion++;
    stop();
    unreadMap.value = {};
    totalUnread.value = 0;
    totalMineUnread.value = 0;
    totalOtherUnread.value = 0;
    newMessageNotices.value = [];
    lastMine = null;
    lastOther = null;
    noticeSequence = 0;
    pendingDetailNoticeKey = null;
  };

  /** 取某账号的三档未读数，没有记录时返回全 0 */
  const unreadOf = (accountId: number): AccountUnread =>
    unreadMap.value[accountId] ?? EMPTY_ACCOUNT_UNREAD;

  return {
    unreadMap,
    newMessageNotices,
    totalUnread,
    totalMineUnread,
    totalOtherUnread,
    unreadOf,
    start,
    stop,
    reset,
  };
});
